import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, SearchIcon, ToggleLeftIcon, ToggleRightIcon, ArrowUpIcon, ArrowDownIcon, FilterIcon, XIcon, TagIcon } from 'lucide-react';
import { Tag } from './TagsManagement';
import { StatusBadge } from '../../StatusBadge';
type SortField = 'name' | 'tagType' | 'scope' | 'active';
type SortDirection = 'asc' | 'desc';
interface TagsListProps {
  tags: Tag[];
  onAdd: () => void;
  onEdit: (tag: Tag) => void;
  onToggleActive: (id: string) => void;
  onDelete: (id: string) => void;
  locale: string;
}
export function TagsList({
  tags,
  onAdd,
  onEdit,
  onToggleActive,
  onDelete,
  locale
}: TagsListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    tagType: '',
    scope: '',
    active: ''
  });
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      tagType: '',
      scope: '',
      active: ''
    });
    setSearchTerm('');
  };
  // Get unique tag types and scopes for filters
  const uniqueTagTypes = Array.from(new Set(tags.filter(tag => tag.tagTypeName).map(tag => tag.tagTypeName)));
  const uniqueScopes = Array.from(new Set(tags.map(tag => tag.scopeType)));
  // Apply filters and sorting
  const filteredTags = tags.filter(tag => {
    // Search term filter
    const searchMatch = tag.name.toLowerCase().includes(searchTerm.toLowerCase()) || tag.description && tag.description.toLowerCase().includes(searchTerm.toLowerCase()) || tag.tagTypeName && tag.tagTypeName.toLowerCase().includes(searchTerm.toLowerCase()) || tag.scopeName && tag.scopeName.toLowerCase().includes(searchTerm.toLowerCase());
    // Tag type filter
    const tagTypeMatch = !filters.tagType || tag.tagTypeName === filters.tagType;
    // Scope filter
    const scopeMatch = !filters.scope || tag.scopeType === filters.scope;
    // Active status filter
    const activeMatch = filters.active === '' || filters.active === 'active' && tag.isActive || filters.active === 'inactive' && !tag.isActive;
    return searchMatch && tagTypeMatch && scopeMatch && activeMatch;
  });
  // Apply sorting
  const sortedTags = [...filteredTags].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'tagType':
        const tagTypeA = a.tagTypeName || '';
        const tagTypeB = b.tagTypeName || '';
        comparison = tagTypeA.localeCompare(tagTypeB);
        break;
      case 'scope':
        const scopeCompare = a.scopeType.localeCompare(b.scopeType);
        if (scopeCompare !== 0) {
          comparison = scopeCompare;
        } else {
          const scopeNameA = a.scopeName || '';
          const scopeNameB = b.scopeName || '';
          comparison = scopeNameA.localeCompare(scopeNameB);
        }
        break;
      case 'active':
        comparison = Number(a.isActive) - Number(b.isActive);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Render tag with its color
  const renderTagBadge = (tag: Tag) => {
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
      backgroundColor: `${tag.colourHex}20`,
      color: tag.colourHex,
      border: `1px solid ${tag.colourHex}`
    }} data-id="element-17801">
        <TagIcon className="w-3 h-3 mr-1" data-id="element-17802" />
        {tag.name}
      </span>;
  };
  return <div className="space-y-6" data-id="element-17803">
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-17804">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-17805">
          <div className="flex-1 max-w-md" data-id="element-17806">
            <div className="relative rounded-md shadow-sm" data-id="element-17807">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17808">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-17809" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search tags..." data-id="element-17810" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-17811">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17812">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-17813" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-17814">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={onAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17815">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-17816" />
              Add Tag
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-17817">
            {filters.tagType && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-17818">
                Type: {filters.tagType}
                <button type="button" onClick={() => setFilters({
            ...filters,
            tagType: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-17819">
                  <XIcon className="h-3 w-3" data-id="element-17820" />
                </button>
              </span>}
            {filters.scope && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-17821">
                Scope: {filters.scope}
                <button type="button" onClick={() => setFilters({
            ...filters,
            scope: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-17822">
                  <XIcon className="h-3 w-3" data-id="element-17823" />
                </button>
              </span>}
            {filters.active && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-17824">
                Status: {filters.active === 'active' ? 'Active' : 'Inactive'}
                <button type="button" onClick={() => setFilters({
            ...filters,
            active: ''
          })} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-17825">
                  <XIcon className="h-3 w-3" data-id="element-17826" />
                </button>
              </span>}
            <button type="button" onClick={resetFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17827">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-3" data-id="element-17828">
            <div data-id="element-17829">
              <label htmlFor="tagType" className="block text-sm font-medium text-gray-700" data-id="element-17830">
                Tag Type
              </label>
              <select id="tagType" name="tagType" value={filters.tagType} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-17831">
                <option value="" data-id="element-17832">All Types</option>
                <option value={null} data-id="element-17833">No Type</option>
                {uniqueTagTypes.map(tagType => <option key={tagType} value={tagType} data-id="element-17834">
                    {tagType}
                  </option>)}
              </select>
            </div>
            <div data-id="element-17835">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-17836">
                Scope
              </label>
              <select id="scope" name="scope" value={filters.scope} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-17837">
                <option value="" data-id="element-17838">All Scopes</option>
                {uniqueScopes.map(scope => <option key={scope} value={scope} data-id="element-17839">
                    {scope}
                  </option>)}
              </select>
            </div>
            <div data-id="element-17840">
              <label htmlFor="active" className="block text-sm font-medium text-gray-700" data-id="element-17841">
                Status
              </label>
              <select id="active" name="active" value={filters.active} onChange={handleFilterChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-17842">
                <option value="" data-id="element-17843">All Statuses</option>
                <option value="active" data-id="element-17844">Active</option>
                <option value="inactive" data-id="element-17845">Inactive</option>
              </select>
            </div>
          </div>}
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17846">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-17847">
          <thead className="bg-gray-50" data-id="element-17848">
            <tr data-id="element-17849">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-17850">
                <div className="flex items-center space-x-1" data-id="element-17851">
                  <span data-id="element-17852">Tag</span>
                  {sortField === 'name' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-17853" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-17854" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('tagType')} data-id="element-17855">
                <div className="flex items-center space-x-1" data-id="element-17856">
                  <span data-id="element-17857">Tag Type</span>
                  {sortField === 'tagType' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-17858" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-17859" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('scope')} data-id="element-17860">
                <div className="flex items-center space-x-1" data-id="element-17861">
                  <span data-id="element-17862">Scope</span>
                  {sortField === 'scope' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-17863" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-17864" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17865">
                Date Range
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('active')} data-id="element-17866">
                <div className="flex items-center space-x-1" data-id="element-17867">
                  <span data-id="element-17868">Status</span>
                  {sortField === 'active' && (sortDirection === 'asc' ? <ArrowUpIcon className="w-4 h-4" data-id="element-17869" /> : <ArrowDownIcon className="w-4 h-4" data-id="element-17870" />)}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17871">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-17872">
            {sortedTags.length === 0 ? <tr data-id="element-17873">
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-17874">
                  No tags found. Click "Add Tag" to create one.
                </td>
              </tr> : sortedTags.map(tag => <tr key={tag.id} className="hover:bg-gray-50" data-id="element-17875">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-17876">
                    <div className="flex flex-col" data-id="element-17877">
                      <div className="mb-1" data-id="element-17878">{renderTagBadge(tag)}</div>
                      {tag.description && <span className="text-xs text-gray-500 truncate max-w-xs" data-id="element-17879">
                          {tag.description}
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17880">
                    {tag.tagTypeName || <span className="text-gray-400 italic" data-id="element-17881">None</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17882">
                    <div data-id="element-17883">
                      {tag.scopeType}
                      {tag.scopeName && <span className="ml-1 text-xs text-gray-400" data-id="element-17884">
                          ({tag.scopeName})
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17885">
                    {tag.activeFrom ? <span data-id="element-17886">
                        {new Date(tag.activeFrom).toLocaleDateString(locale)}
                        {tag.activeTo ? <span data-id="element-17887">
                            {' '}
                            to{' '}
                            {new Date(tag.activeTo).toLocaleDateString(locale)}
                          </span> : <span className="text-gray-400 italic" data-id="element-17888">
                            {' '}
                            - Ongoing
                          </span>}
                      </span> : <span className="text-gray-400 italic" data-id="element-17889">
                        No date restriction
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-17890">
                    <StatusBadge status={tag.isActive ? 'success' : 'error'} text={tag.isActive ? 'Active' : 'Inactive'} data-id="element-17891" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17892">
                    <div className="flex justify-end space-x-2" data-id="element-17893">
                      <button onClick={() => onEdit(tag)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-17894">
                        <EditIcon className="h-4 w-4" data-id="element-17895" />
                      </button>
                      <button onClick={() => onToggleActive(tag.id)} className={`${tag.isActive ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`} title={tag.isActive ? 'Deactivate' : 'Activate'} data-id="element-17896">
                        {tag.isActive ? <ToggleRightIcon className="h-4 w-4" data-id="element-17897" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-17898" />}
                      </button>
                      <button onClick={() => onDelete(tag.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-17899">
                        <TrashIcon className="h-4 w-4" data-id="element-17900" />
                      </button>
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}